import { QueryClient } from '@tanstack/react-query'
import {
  fetchResource,
  MutationOptions,
  QueryOptions,
  useMutateResource,
  useQueryResource,
} from '../resource'
import {
  CreateQuizDocument,
  CreateQuizMutation,
  CreateQuizMutationVariables,
  QuizAttemptFragment,
  QuizAttemptStatusEnum,
  QuizDocument,
  QuizFragment,
  QuizInput,
  QuizQuery,
  QuizQueryVariables,
} from '../types'

type CreateQuizOptions = MutationOptions<
  CreateQuizMutation,
  CreateQuizMutationVariables
>

type QuizQueryOptions = QueryOptions<QuizQuery>

// type UpdateQuizOptions = MutationOptions<
//   UpdateQuizMutation,
//   UpdateQuizMutationVariables
// >

export type QuizType = ReturnType<typeof deriveQuiz>

/**
 * @private
 * The cache key name utilized for the `Quiz` Resource.
 */

const KEY_NAME = 'Quiz'

/**
 * @private
 * Derives data not contained in the API.
 */
function deriveQuiz(quiz?: QuizFragment) {
  if (quiz == null) return quiz
  const emptyQuizState = quiz?.currentUserQuizAttempts?.length === 0
  const inProgressQuizAttempt = quiz?.currentUserQuizAttempts?.find(
    (quizAttempt: QuizAttemptFragment) =>
      quizAttempt.status === QuizAttemptStatusEnum.Started,
  )
  const currentQuestion =
    inProgressQuizAttempt?.userCurrentQuestionAttempt?.quizQuestion
  const mostRecentSubmittedQuizAttempt = quiz?.currentUserQuizAttempts
    ?.filter(
      (quizAttempt: QuizAttemptFragment) =>
        quizAttempt.status === QuizAttemptStatusEnum.Submitted,
    )
    .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))?.[0]

  const inProgressPercentage = emptyQuizState
    ? '0'
    : ((currentQuestion?.sortPosition ?? 0) / (quiz?.totalQuestions ?? 10)) *
      100
  const gradePercentage = mostRecentSubmittedQuizAttempt?.grade || 0
  const progressWidthInQuiz = Math.floor(Number(inProgressPercentage) / 10) * 10
  const progressWidthAfterComplete = Math.floor(gradePercentage / 10) * 10
  const numberOfQuestionsCorrect = Math.ceil(
    (gradePercentage / 100) * (quiz?.totalQuestions || 10),
  )

  return {
    ...quiz,
    currentQuestion,
    emptyQuizState,
    inProgressQuizAttempt,
    mostRecentSubmittedQuizAttempt,
    numberOfQuestionsCorrect,
    progressWidthInQuiz,
    progressWidthAfterComplete,
    userPassed:
      mostRecentSubmittedQuizAttempt?.grade != null &&
      mostRecentSubmittedQuizAttempt?.grade >= 80,
    // timeToCompleteWords: quiz.timeToComplete ? dateAndTimeToWords(quiz.timeToComplete) : null,
  }
}
/**
 * @private
 * Serializes a QuizFragment into a QuizInput.
 *
 * @TODO: Quiz mutations aren't ready on the BE yet... but when they are...
 */
function serializeFragmentToInput(quiz: QuizFragment): QuizInput {
  if (quiz == null) return quiz

  return {
    description: quiz.description,
    displayname: 'vangst', // String
    isPublished: false, // Boolean
    quizQuestionsAttributes: [], // ReadonlyArray<QuizQuestionInput>
    timeToComplete: quiz.timeToComplete || 0, // Int
    title: quiz.title,
    totalCorrectToGainCertification: quiz.totalCorrectToGainCertification || 0, // Int
    videoUrl: quiz.videoUrl,
  }
}

/**
 * @public
 * A fetcher for the current `Quiz` with derived data.
 *
 * @example
 * const quiz = await fetchQuiz(queryClient)
 * const timeToComplete = quiz?.getQuiz?.timeToComplete */
export async function fetchQuiz(
  queryClient: QueryClient,
  variables?: QuizQueryVariables,
) {
  const result = await fetchResource<QuizQuery, QuizQueryVariables>(
    queryClient,
    KEY_NAME,
    QuizDocument,
    variables,
  )
  return result ? { getQuiz: deriveQuiz(result.getQuiz) } : result
}

/**
 * @public
 * A fetcher for the current `Quiz` which is serialized into a `QuizInput`.
 *
 * @example
 * const quiz = await fetchQuizInput(queryClient)
 * const defaultFormValues = quiz?.getQuiz
 */
export async function fetchQuizInput(
  queryClient: QueryClient,
  variables?: QuizQueryVariables,
) {
  const result = await fetchResource<QuizQuery, QuizQueryVariables>(
    queryClient,
    KEY_NAME,
    QuizDocument,
    variables,
  )

  return result?.getQuiz != null
    ? { getQuiz: serializeFragmentToInput(result.getQuiz) }
    : result
}

/**
 * @public
 * A fetching hook for the current `Quiz` with derived data.
 * Passing `initialData` will hydrate the cache.
 *
 * @example
 * const { data } = useQuiz()
 * const timeToComplete = data?.getQuiz?.timeToComplete
 */
export function useQuiz(
  variables?: QuizQueryVariables,
  options?: QuizQueryOptions,
) {
  const query = useQueryResource<QuizQuery, QuizQueryVariables>(
    KEY_NAME,
    QuizDocument,
    variables,
    { ...options },
  )
  const data =
    query?.data?.getQuiz != null
      ? { getQuiz: deriveQuiz(query.data.getQuiz) }
      : undefined
  return { ...query, data }
}

/**
 * @public
 * A fetching hook for the current `Quiz` which is serialized into a `QuizInput`.
 * Passing `initialData` will hydrate the cache.
 *
 * @example
 * const { data } = useQuizInput()
 * const { handleSubmit } = useForm<QuizInput>({ defaultValues: data?.getQuiz })
 */
export function useQuizInput(
  variables?: QuizQueryVariables,
  options?: QuizQueryOptions,
) {
  const query = useQueryResource<QuizQuery, QuizQueryVariables>(
    KEY_NAME,
    QuizDocument,
    variables,
    { staleTime: Infinity, ...options },
  )
  const data =
    query?.data?.getQuiz != null
      ? { getQuiz: serializeFragmentToInput(query.data.getQuiz) }
      : undefined
  return { ...query, data }
}

// ------------------------------------

/**
 * @public
 * A hook mutation to create a `Quiz` resource.
 *
 * @example
 * const createQuiz = useCreateQuiz()
 * const changeQuiz = async () => {
 *   const input = { quiz: { title: 'Learn about a moose.', ... } }
 *   createQuiz.mutate({input})
 * }
 */
export function useCreateQuiz(options?: CreateQuizOptions) {
  const result = useMutateResource<
    CreateQuizMutation,
    CreateQuizMutationVariables
  >(KEY_NAME + 'Create', CreateQuizDocument, options)
  const data =
    result.data?.createQuiz != null
      ? { createQuiz: deriveQuiz(result.data.createQuiz) }
      : undefined
  return { ...result, data }
}

/**
 * @public
 * A mutation to update a `Quiz` resource.
 * The resource will be created if one doesn't exist.
 *
 * @example
 * const updateQuiz = useUpdateQuiz()
 * const changeQuiz = async () => {
 *   const input = { quiz: { title: 'Learn about meese.', ... } }
 *   updateQuiz.mutate({input})
 * }
 */
// export function useUpdateQuiz(options?: UpdateQuizOptions) {
//   const result = useMutateResource<
//     UpdateQuizMutation,
//     UpdateQuizMutationVariables
//   >(KEY_NAME + 'Update', UpdateQuizDocument, options)
//   const data = result.data.updateQuiz != null
//     ? { updateQuiz: deriveQuiz(result.data.updateQuiz) }
//     : undefined
//   return { ...result, data }
// }
